import {
  Button,
  Card,
  CardContent,
  CardHeader,
  FormPageLayout,
  Skeleton,
  toast,
  useApp,
} from '@startupquickstart/frontend';
import { ArrowLeft, Save } from 'lucide-react';
import { useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { SectionNav } from './SectionNav';
import {
  getVisibleSections,
  type ChallengeEditorSectionId,
} from './sectionRegistry';
import { AccessCodeSection } from './sections/AccessCodeSection';
import { ContentSection } from './sections/ContentSection';
import { DetailsSection } from './sections/DetailsSection';
import { GameplaySection } from './sections/GameplaySection';
import { LocationSection } from './sections/LocationSection';
import { MediaSection } from './sections/MediaSection';
import { MinigameSection } from './sections/MinigameSection';
import { RewardsSection } from './sections/RewardsSection';
import { VoiceoverSection } from './sections/VoiceoverSection';
import type { ChallengeEditorContext, ChallengeEditorSectionProps } from './types';
import { useChallengeEditorForm } from './useChallengeEditorForm';

export interface ChallengeEditorProps {
  context: ChallengeEditorContext;
  /** Undefined in create mode. */
  challengeId?: string;
  /** Required when `context === 'event'`. */
  eventId?: string;
  /** Pre-selected group for event create (from `?groupId=`). */
  groupId?: string | null;
}

/** Number of skeleton section cards shown while an entity loads. */
const SKELETON_SECTION_COUNT = 4;

function EditorSkeleton() {
  return (
    <FormPageLayout>
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-60" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-32" />
        </div>
      </FormPageLayout.Header>
      <FormPageLayout.Content maxWidth="6xl">
        {Array.from({ length: SKELETON_SECTION_COUNT }, (_, i) => (
          <Card key={i} className="rounded-xl border bg-card shadow-sm">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-64" />
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ))}
      </FormPageLayout.Content>
    </FormPageLayout>
  );
}

/**
 * The unified challenge editor: one sectioned page for both the library and
 * event contexts, create and edit. Sections come from the section registry;
 * a sticky nav rail tracks scroll position and per-section completeness.
 * A single Save runs the whole pipeline (validate → main save → dirty
 * sub-resource saves → cache invalidation → navigate).
 */
export function ChallengeEditor({ context, challengeId, eventId, groupId }: ChallengeEditorProps) {
  const navigate = useNavigate();
  const { config } = useApp();

  const isEvent = context === 'event';
  const listPath = isEvent ? `/events/${eventId}/challenges` : '/challenges';

  const handleSaved = useCallback(
    (entityId: string) => {
      navigate(isEvent ? listPath : `/challenges/${entityId}`);
    },
    [navigate, isEvent, listPath],
  );

  const handleNotFound = useCallback(() => {
    toast.error('Challenge not found');
    navigate(listPath);
  }, [navigate, listPath]);

  const {
    values,
    errors,
    updateField,
    submit,
    isLoading,
    isSaving,
    isFormReady,
    lastSaved,
    mode,
    registerSectionHandle,
  } = useChallengeEditorForm({
    context,
    challengeId,
    eventId,
    groupId,
    onSaved: handleSaved,
    onNotFound: handleNotFound,
  });

  const isEdit = mode === 'edit';

  const handleCancel = () => {
    if (!isEvent && isEdit && challengeId) {
      navigate(`/challenges/${challengeId}`);
      return;
    }
    navigate(listPath);
  };

  if (isEdit && (isLoading || !isFormReady)) {
    return <EditorSkeleton />;
  }

  const sectionProps: ChallengeEditorSectionProps = {
    values,
    errors,
    updateField,
    context,
    mode,
  };

  // Section-id → renderer. Registry-shaped so adding a section is a data
  // edit here + in `sectionRegistry.ts`.
  const sectionRenderers: Record<ChallengeEditorSectionId, () => ReactNode> = {
    details: () => <DetailsSection {...sectionProps} />,
    content: () => <ContentSection {...sectionProps} />,
    minigame: () => <MinigameSection {...sectionProps} />,
    location: () => (
      <LocationSection {...sectionProps} googleMapsApiKey={config?.googleMapsApiKey} />
    ),
    media: () => <MediaSection {...sectionProps} />,
    accessCode: () => <AccessCodeSection {...sectionProps} />,
    gameplay: () => <GameplaySection {...sectionProps} />,
    rewards: () =>
      eventId && challengeId ? (
        <RewardsSection
          eventId={eventId}
          challengeId={challengeId}
          registerSectionHandle={registerSectionHandle}
        />
      ) : null,
    voiceover: () =>
      challengeId ? (
        <VoiceoverSection
          values={values}
          updateField={updateField}
          context={context}
          challengeId={challengeId}
          eventId={eventId}
        />
      ) : null,
  };

  const visibleSections = getVisibleSections(context, mode, values);

  const title = isEdit ? 'Edit Challenge' : 'Create Challenge';
  const subtitle = isEdit
    ? values.name || 'Update challenge details'
    : isEvent
      ? 'Add a new challenge to this event'
      : 'Add a new challenge to the library';
  const saveLabel = isEdit ? 'Save Changes' : 'Create Challenge';

  const actions = (
    <div className="flex items-center gap-3">
      <Button type="button" variant="ghost" onClick={handleCancel}>
        Cancel
      </Button>
      <Button type="button" onClick={submit} loading={isSaving} icon={Save}>
        {saveLabel}
      </Button>
    </div>
  );

  return (
    <FormPageLayout>
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            aria-label="Back"
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">{title}</h1>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        {actions}
      </FormPageLayout.Header>

      <FormPageLayout.Content maxWidth="6xl">
        <div className="grid gap-8 lg:grid-cols-[11rem_minmax(0,1fr)]">
          <SectionNav sections={visibleSections} values={values} errors={errors} />
          <div className="space-y-6 min-w-0">
            {visibleSections.map((section) => (
              <div key={section.id}>{sectionRenderers[section.id]()}</div>
            ))}
          </div>
        </div>
      </FormPageLayout.Content>

      <FormPageLayout.Footer maxWidth="6xl" lastSaved={lastSaved}>
        {actions}
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
